import { Box, makeStyles } from '@material-ui/core'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import { CloseRounded } from '@material-ui/icons'
import Text from '../../../../../atoms/Text'
import { TEXT_VARIANTS } from '../../../../../../constants'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import Checkbox from '../../../../../molecules/Checkbox'
import { usePreviewDisplayContext } from '../../components/PreviewDisplayContext'
import SydButton from '../../../../../commonDesign/Button'
import { InfographicBlockList } from './InfographicBlockList'

const useStyles = makeStyles({
  selectedBlockContainer: {
    position: 'absolute',
    right: '634px',
    top: '40px',
    zIndex: 10,
    width: '600px',
    transform: 'translateX(100%)',
    transition: '.25s ease-in-out',
    border: 'var(--border-neutral-normal)',
    borderRadius: 'var(--border-radius-md)',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: 'var(--shadow-md)',
    overflow: 'hidden',
    padding: 'var(--padding-md)',
    background: 'var(--card-bg-color)',
    '&.active': {
      transform: 'translateX(0)',
      transition: '.25s ease-in-out'
    }
  }
})

export const SelectedBlockEditCard = () => {
  const classes = useStyles()
  const { getValues, setValue } = useFormContext()
  const { selectedBlockId, setSelectedBlockId, currentPageContent, currentPageIndex } = usePreviewDisplayContext()
  const block = currentPageContent?.find(b => b.key === selectedBlockId)

  const onBlockChange = (key, value) => {
    const existingContent = getValues('content')
    const newValue = {
      ...existingContent,
      pages: existingContent.pages.map((p, pi) => {
        if (pi !== currentPageIndex) return p
        return {
          ...p,
          blocks: currentPageContent.map((b) => b.key === selectedBlockId ? {
            ...b,
            [key]: value
          } : b)?.filter(x => !x.isEmptyBlock) ?? []
        }
      })
    }
    return setValue('content', newValue)
  }

  const onAddBlockToPage = (newBlock) => {
    const existingContent = getValues('content')
    const newValue = {
      ...existingContent,
      pages: existingContent.pages.map((p, pi) => {
        if (pi !== currentPageIndex) return p
        return {
          ...p,
          blocks: currentPageContent.map((b) => {
            if (b.key !== selectedBlockId) return b

            const size = {
              cols: newBlock.metadata.size.cols + 2 <= b.size.cols ? newBlock.metadata.size.cols : b.size.cols,
              rows: newBlock.metadata.size.rows + 2 <= b.size.rows ? newBlock.metadata.size.rows : b.size.rows
            }

            return {
              ...b,
              size,
              key: newBlock.key,
              infographicBlockId: newBlock.infographicBlockId,
              infographicBlock: newBlock,
              isEmptyBlock: false
            }
          })?.filter(x => !x.isEmptyBlock) ?? []
        }
      })
    }
    setSelectedBlockId(newBlock.key)
    setValue('content', newValue)
  }

  if (block?.isEmptyBlock) {
    return (
      <div className={clsx(classes.selectedBlockContainer, 'active')} style={{ padding: 0 }}>
        <InfographicBlockList
          sizeFilter={block?.size}
          onAddBlockToPage={onAddBlockToPage}
        />
      </div>
    )
  }

  return (
    <div className={clsx(classes.selectedBlockContainer, { active: !!block })}>
      <div className='mb-2 flex gap-2 justify-between items-start'>
        <div>
          <Text variant={TEXT_VARIANTS.h3} text='Selected Block Properties' />
          <Text variant={TEXT_VARIANTS.caption} text={`Change display settings for ${block?.infographicBlock?.name}`} />
        </div>

        <SydButton
          type='button'
          size='xs'
          onClick={() => setSelectedBlockId(null)}
          style={{ minWidth: 0, padding: 'var(--padding-xs)' }}
        >
          <CloseRounded fontSize='small' />
        </SydButton>
      </div>

      <SydLabel label='Coordinates' style={{ fontSize: '14px' }} />
      <Box display='flex' gridGap='12px'>
        <Box flex={1}>
          <SydLabel
            label='Row'
            description={(
              <>
                Determines what row this item starts on.<br />
                (Min: 0 / Max: {16 - block?.size?.rows})
              </>
            )}
          >
            <SydInput
              size='sm'
              type='number'
              minWidth='100%'
              inputProps={{
                increment: 1,
                min: 0,
                max: 16 - block?.size?.rows
              }}
              value={block?.cords?.y}
              onChange={(e) => onBlockChange('cords', {
                ...block?.cords,
                y: parseInt(e.target.value)
              })}
            />
          </SydLabel>
        </Box>
        <Box flex={1}>
          <SydLabel
            label='Column'
            description={(
              <>
                Determines what column this item starts on.<br />
                (Min: 0 / Max: {12 - block?.size?.cols})
              </>
            )}
          >
            <SydInput
              size='sm'
              type='number'
              minWidth='100%'
              inputProps={{
                increment: 1,
                min: 0,
                max: 12 - block?.size?.cols
              }}
              value={block?.cords?.x}
              onChange={(e) => onBlockChange('cords', {
                ...block?.cords,
                x: parseInt(e.target.value)
              })}
            />
          </SydLabel>
        </Box>
      </Box>
      <hr style={{ margin: '1rem 0' }} />
      <SydLabel label='Size' style={{ fontSize: '14px' }} />
      <Box display='flex' gridGap='12px'>
        <Box flex={1}>
          <SydLabel
            label='Height (Rows)'
            description={(
              <>
                Determines how many rows this block takes up<br />
                (Min: {Math.min(block?.infographicBlock?.metadata?.size?.rows ?? 0, 16 - block?.cords.y)} /
                Max: {16 - block?.cords?.y})
              </>
            )}
          >
            <SydInput
              size='sm'
              type='number'
              minWidth='100%'
              disabled={block?.infographicBlock?.metadata?.size?.rows >= (16 - block?.cords?.y)}
              inputProps={{
                increment: 1,
                min: block?.infographicBlock?.metadata?.size?.rows ?? 0,
                max: (16 - block?.cords?.y)
              }}
              value={block?.size?.rows}
              onChange={(e) => onBlockChange('size', {
                ...block?.size,
                rows: parseInt(e.target.value)
              })}
            />
          </SydLabel>
        </Box>
        <Box flex={1}>
          <SydLabel
            label='Width (Columns)'
            description={(
              <>
                Determines how many columns this block takes up<br />
                (Min: {block?.infographicBlock?.metadata?.size?.cols ?? 0} /
                Max: {12 - block?.cords?.x})
              </>
            )}
          >
            <SydInput
              size='sm'
              type='number'
              minWidth='100%'
              disabled={block?.infographicBlock?.metadata?.size?.cols === 12 - block?.cords?.x}
              inputProps={{
                increment: 1,
                min: block?.infographicBlock?.metadata?.size?.cols ?? 0,
                max: (12 - block?.cords?.x)
              }}
              value={block?.size?.cols}
              onChange={(e) => {
                return onBlockChange('size', {
                  ...block?.size,
                  cols: parseInt(e.target.value)
                })
              }}
            />
          </SydLabel>
        </Box>
      </Box>

      <hr style={{ margin: '1rem 0' }} />

      <Box display='flex' gridGap={4}>
        <Checkbox
          checked={!!block?.hideInHtml}
          onChange={(e, checked) => onBlockChange('hideInHtml', checked)}
        />
        <Box onClick={() => onBlockChange('hideInHtml', !block?.hideInHtml)}>
          <SydLabel
            label='Hide in HTML Delivery'
            description='Will only show in PDF Delivery / export'
          />
        </Box>
      </Box>
    </div>
  )
}
